import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { categoryAPI, menuItemAPI, modifierGroupAPI, tableAPI, branchAPI, statsAPI } from '../services';
import { toast } from 'react-toastify';

const AdminMenuContext = createContext(null);

export const useAdminMenu = () => {
  const context = useContext(AdminMenuContext);
  if (!context) {
    throw new Error('useAdminMenu must be used within AdminMenuProvider');
  }
  return context;
};

export const AdminMenuProvider = ({ children }) => {
  // State
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const normalizeCategoryRecord = useCallback((category) => {
    if (!category) return null;
    return {
      id: category.id,
      name: category.name,
      slug: category.slug,
      description: category.description,
      image: category.image,
      displayOrder: category.displayOrder !== undefined ? category.displayOrder : category.display_order,
      isActive: category.isActive !== undefined ? category.isActive : category.is_active,
      createdAt: category.createdAt || category.created_at,
      updatedAt: category.updatedAt || category.updated_at,
      itemCount:
        category.itemCount !== undefined
          ? category.itemCount
          : category.item_count !== undefined
          ? category.item_count
          : category._count?.menu_items || 0,
    };
  }, []);

  const normalizeMenuItemRecord = useCallback((item) => {
    if (!item) return null;
    return {
      id: item.id,
      name: item.name,
      slug: item.slug,
      description: item.description,
      price: item.price,
      image: item.image,
      categoryId: item.categoryId || item.category_id,
      enabledModifierGroupIds: item.enabledModifierGroupIds || item.enabled_modifier_group_ids || [],
      isAvailable: item.isAvailable !== undefined ? item.isAvailable : item.is_available,
      isFeatured: item.isFeatured !== undefined ? item.isFeatured : item.is_featured,
      displayOrder: item.displayOrder !== undefined ? item.displayOrder : item.display_order,
      tags: item.tags || [],
      createdBy: item.createdBy || item.created_by || null,
      creator: item.creator || null,
      createdAt: item.createdAt || item.created_at,
      updatedAt: item.updatedAt || item.updated_at,
      // Menu training fields (modal + badge đọc snake_case)
      requires_training:
        item.requires_training !== undefined
          ? item.requires_training
          : (item.requiresTraining ?? false),
      confirmation_type:
        item.confirmation_type || item.confirmationType || 'SAME_SHIFT_PEER',
      training_auto_locked:
        item.training_auto_locked !== undefined
          ? item.training_auto_locked
          : (item.trainingAutoLocked ?? false),
      videoUrls: item.videoUrls || item.video_urls || [],
      recipeHtml: item.recipeHtml !== undefined ? item.recipeHtml : (item.recipe_html ?? ''),
      approval_status: item.approval_status || item.approvalStatus || 'DRAFT',
      released_at: item.released_at || item.releasedAt || null,
      rnd_project: item.rnd_project || null,
    };
  }, []);

  const [modifierGroups, setModifierGroups] = useState([]);
  const [tables, setTables] = useState([]);
  const [branches, setBranches] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const normalizeModifierOption = useCallback((option) => {
    if (!option) return null;
    return {
      id: option.id,
      modifierGroupId: option.modifierGroupId || option.modifier_group_id,
      name: option.name,
      price: option.price,
      isDefault: option.isDefault !== undefined ? option.isDefault : option.is_default,
      displayOrder: option.displayOrder !== undefined ? option.displayOrder : option.display_order,
      createdAt: option.createdAt || option.created_at,
      updatedAt: option.updatedAt || option.updated_at,
    };
  }, []);

  const normalizeModifierGroupRecord = useCallback(
    (group) => {
      if (!group) return null;
      return {
        id: group.id,
        name: group.name,
        type: (group.type || '').toLowerCase(),
        required: group.required !== undefined ? group.required : Boolean(group.is_required),
        displayOrder: group.displayOrder !== undefined ? group.displayOrder : group.display_order,
        isActive: group.isActive !== undefined ? group.isActive : group.is_active,
        createdAt: group.createdAt || group.created_at,
        updatedAt: group.updatedAt || group.updated_at,
        options: (group.options || []).map((option) => normalizeModifierOption(option)).filter(Boolean),
      };
    },
    [normalizeModifierOption],
  );

  const normalizeTableRecord = useCallback((table) => {
    if (!table) return null;

    const branchId = table.branchId || table.branch_id || table.storeId || table.branch?.id || '';

    return {
      id: table.id,
      branchId,
      storeId: table.storeId || branchId,
      number: table.number != null ? Number(table.number) : 0,
      slug: table.slug,
      capacity: table.capacity,
      isAvailable: table.isAvailable !== undefined ? table.isAvailable : table.is_available,
      isActive: table.isActive !== undefined ? table.isActive : table.is_active,
      roomId: table.roomId !== undefined ? table.roomId : (table.room_id ?? null),
      cleanupActionsOverride:
        table.cleanupActionsOverride ?? table.cleanup_actions_override ?? [],
      room: table.room || null,
      createdAt: table.createdAt || table.created_at,
      updatedAt: table.updatedAt || table.updated_at,
      branch: table.branch || null,
    };
  }, []);

  // ===================
  // CATEGORIES
  // ===================

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await categoryAPI.getCategories();
      const normalized = (response.data || []).map((category) => normalizeCategoryRecord(category)).filter(Boolean);
      setCategories(normalized);
    } catch (err) {
      setError(err);
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, [normalizeCategoryRecord]);

  const createCategory = useCallback(
    async (categoryData) => {
      try {
        setLoading(true);
        const response = await categoryAPI.createCategory(categoryData);
        const normalized = normalizeCategoryRecord(response.data);
        setCategories((prev) => (normalized ? [...prev, normalized] : prev));
        toast.success('Tạo danh mục thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeCategoryRecord],
  );

  const updateCategory = useCallback(
    async (id, updateData) => {
      try {
        setLoading(true);
        const response = await categoryAPI.updateCategory(id, updateData);
        const normalized = normalizeCategoryRecord(response.data);
        setCategories((prev) => prev.map((cat) => (cat.id === id ? normalized || cat : cat)));
        toast.success('Cập nhật danh mục thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeCategoryRecord],
  );

  const deleteCategory = useCallback(async (id) => {
    try {
      setLoading(true);
      await categoryAPI.deleteCategory(id);
      setCategories((prev) => prev.filter((cat) => cat.id !== id));
      // Also remove menu items in this category
      setMenuItems((prev) => prev.filter((item) => item.categoryId !== id));
      toast.success('Xóa danh mục thành công!');
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reorderCategories = useCallback(async (newCategories) => {
    try {
      const categoryIds = newCategories.map((cat) => cat.id);
      await categoryAPI.reorderCategories(categoryIds);
      setCategories(newCategories);
      toast.success('Sắp xếp danh mục thành công!');
    } catch (err) {
      setError(err);
      toast.error('Lỗi khi sắp xếp danh mục');
      throw err;
    }
  }, []);

  // ===================
  // MENU ITEMS
  // ===================

  const fetchMenuItems = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);
        const response = await menuItemAPI.getMenuItems(params);
        const normalized = (response.data || []).map((item) => normalizeMenuItemRecord(item)).filter(Boolean);
        setMenuItems(normalized);
      } catch (err) {
        setError(err);
        console.error('Error fetching menu items:', err);
      } finally {
        setLoading(false);
      }
    },
    [normalizeMenuItemRecord],
  );

  const createMenuItem = useCallback(
    async (itemData) => {
      try {
        setLoading(true);
        const response = await menuItemAPI.createMenuItem(itemData);
        const normalized = normalizeMenuItemRecord(response.data);
        setMenuItems((prev) => (normalized ? [...prev, normalized] : prev));
        toast.success('Tạo món thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeMenuItemRecord],
  );

  const updateMenuItem = useCallback(
    async (id, updateData) => {
      try {
        setLoading(true);
        const response = await menuItemAPI.updateMenuItem(id, updateData);
        const normalized = normalizeMenuItemRecord(response.data);
        setMenuItems((prev) => prev.map((item) => (item.id === id ? normalized || item : item)));
        toast.success('Cập nhật món thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeMenuItemRecord],
  );

  const deleteMenuItem = useCallback(async (id) => {
    try {
      setLoading(true);
      await menuItemAPI.deleteMenuItem(id);
      setMenuItems((prev) => prev.filter((item) => item.id !== id));
      toast.success('Xóa món thành công!');
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reorderMenuItems = useCallback(async (categoryId, newMenuItems) => {
    try {
      const menuItemIds = newMenuItems.map((item) => item.id);
      await menuItemAPI.reorderMenuItems(categoryId, menuItemIds);
      // Update local state
      setMenuItems((prev) => {
        const otherItems = prev.filter((item) => item.categoryId !== categoryId);
        return [...otherItems, ...newMenuItems];
      });
      toast.success('Sắp xếp món thành công!');
    } catch (err) {
      setError(err);
      toast.error('Lỗi khi sắp xếp món');
      throw err;
    }
  }, []);

  // ===================
  // MODIFIER GROUPS
  // ===================

  const fetchModifierGroups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await modifierGroupAPI.getModifierGroups();
      const normalized = (response.data || []).map((group) => normalizeModifierGroupRecord(group)).filter(Boolean);
      setModifierGroups(normalized);
    } catch (err) {
      setError(err);
      console.error('Error fetching modifier groups:', err);
    } finally {
      setLoading(false);
    }
  }, [normalizeModifierGroupRecord]);

  const createModifierGroup = useCallback(
    async (groupData) => {
      try {
        setLoading(true);
        const response = await modifierGroupAPI.createModifierGroup(groupData);
        const normalized = normalizeModifierGroupRecord(response.data);
        setModifierGroups((prev) => (normalized ? [...prev, normalized] : prev));
        toast.success('Tạo nhóm tùy chọn thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeModifierGroupRecord],
  );

  const updateModifierGroup = useCallback(
    async (id, updateData) => {
      try {
        setLoading(true);
        const response = await modifierGroupAPI.updateModifierGroup(id, updateData);
        const normalized = normalizeModifierGroupRecord(response.data);
        setModifierGroups((prev) => prev.map((group) => (group.id === id ? normalized || group : group)));
        toast.success('Cập nhật nhóm tùy chọn thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeModifierGroupRecord],
  );

  const deleteModifierGroup = useCallback(async (id) => {
    try {
      setLoading(true);
      await modifierGroupAPI.deleteModifierGroup(id);
      setModifierGroups((prev) => prev.filter((group) => group.id !== id));
      // Remove from menu items
      setMenuItems((prev) =>
        prev.map((item) => ({
          ...item,
          enabledModifierGroupIds: (item.enabledModifierGroupIds || []).filter((gid) => gid !== id),
        })),
      );
      toast.success('Xóa nhóm tùy chọn thành công!');
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const reorderModifierGroups = useCallback(async (newGroups) => {
    try {
      const groupIds = newGroups.map((g) => g.id);
      await modifierGroupAPI.reorderModifierGroups(groupIds);
      setModifierGroups(newGroups);
      toast.success('Sắp xếp nhóm tùy chọn thành công!');
    } catch (err) {
      setError(err);
      toast.error('Lỗi khi sắp xếp nhóm tùy chọn');
      throw err;
    }
  }, []);

  // ===================
  // TABLES
  // ===================

  const fetchTables = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);
        const response = await tableAPI.getTables(params);
        const normalized = (response.data || []).map((table) => normalizeTableRecord(table)).filter(Boolean);
        setTables(normalized);
      } catch (err) {
        setError(err);
        console.error('Error fetching tables:', err);
      } finally {
        setLoading(false);
      }
    },
    [normalizeTableRecord],
  );

  const createTable = useCallback(
    async (tableData) => {
      try {
        setLoading(true);
        const response = await tableAPI.createTable(tableData);
        const normalized = normalizeTableRecord(response.data);
        setTables((prev) => (normalized ? [...prev, normalized] : prev));
        toast.success('Tạo bàn thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeTableRecord],
  );

  const updateTable = useCallback(
    async (id, updateData) => {
      try {
        setLoading(true);
        const response = await tableAPI.updateTable(id, updateData);
        const normalized = normalizeTableRecord(response.data);
        setTables((prev) => prev.map((table) => (table.id === id ? normalized || table : table)));
        toast.success('Cập nhật bàn thành công!');
        return normalized;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [normalizeTableRecord],
  );

  const deleteTable = useCallback(async (id) => {
    try {
      setLoading(true);
      await tableAPI.deleteTable(id);
      setTables((prev) => prev.filter((table) => table.id !== id));
      toast.success('Xóa bàn thành công!');
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTableAvailability = useCallback(async (id, isAvailable) => {
    try {
      await tableAPI.updateTableAvailability(id, isAvailable);
      setTables((prev) => prev.map((table) => (table.id === id ? { ...table, isAvailable } : table)));
      toast.success('Cập nhật trạng thái bàn thành công!');
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  // ===================
  // BRANCHES (STORES)
  // ===================

  const fetchBranches = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const response = await branchAPI.getBranches(params);
      setBranches(response.data || []);
    } catch (err) {
      setError(err);
      console.error('Error fetching branches:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ===================
  // STATISTICS
  // ===================

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setStatsError(null);
      const response = await statsAPI.getOverview();
      setStats(response);
    } catch (err) {
      setStatsError(err);
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize data (menu items will be fetched by component with params)
  useEffect(() => {
    fetchCategories();
    fetchModifierGroups();
    fetchBranches();
    fetchStats();
  }, [fetchCategories, fetchModifierGroups, fetchBranches, fetchStats]);

  const value = {
    // State
    categories,
    menuItems,
    modifierGroups,
    tables,
    branches,
    stats,
    statsError,
    loading,
    error,

    // Category actions
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,

    // Menu Item actions
    fetchMenuItems,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    reorderMenuItems,

    // Modifier Group actions
    fetchModifierGroups,
    createModifierGroup,
    updateModifierGroup,
    deleteModifierGroup,
    reorderModifierGroups,

    // Table actions
    fetchTables,
    createTable,
    updateTable,
    deleteTable,
    updateTableAvailability,

    // Branch actions
    fetchBranches,

    // Stats actions
    fetchStats,
  };

  return <AdminMenuContext.Provider value={value}>{children}</AdminMenuContext.Provider>;
};
